/**
 * Hotel Data Processing Utilities
 * Pure functions for hotel-related data transformations
 */
import { normalizeText } from './dataUtils';

/**
 * Booking filter types
 */
export const BOOKING_FILTERS = {
    ALL: 'all',
    CURRENT: 'current',
    UPCOMING: 'upcoming',
    PAST: 'past'
};

/**
 * Filter bookings by search term (dog name, breed, owner name)
 * @param {Array} bookings - Array of bookings to filter
 * @param {string} searchTerm - Search term
 * @returns {Array} Filtered bookings array
 */
export function filterBookingsBySearch(bookings, searchTerm) {
    if (!searchTerm) return bookings;

    const lowerSearch = normalizeText(searchTerm);
    return bookings.filter(booking =>
        normalizeText(booking.dog?.name).includes(lowerSearch) ||
        normalizeText(booking.dog?.breed).includes(lowerSearch) ||
        normalizeText(booking.dog?.owners?.[0]?.name).includes(lowerSearch)
    );
}

/**
 * Filter bookings by date status
 * @param {Array} bookings - Array of bookings to filter
 * @param {string} filterType - Filter type ('all', 'current', 'upcoming', 'past')
 * @returns {Array} Filtered bookings array
 */
export function filterBookingsByStatus(bookings, filterType) {
    if (filterType === BOOKING_FILTERS.ALL) return bookings;

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const toDateStr = (d) => new Date(d).toISOString().slice(0, 10);

    return bookings.filter(booking => {
        const checkIn = toDateStr(booking.checkIn);
        const checkOut = toDateStr(booking.checkOut);
        switch (filterType) {
            case BOOKING_FILTERS.CURRENT:  return checkIn <= todayStr && checkOut >= todayStr;
            case BOOKING_FILTERS.UPCOMING: return checkIn > todayStr;
            case BOOKING_FILTERS.PAST:     return checkOut < todayStr;
            default: return true;
        }
    });
}

/**
 * Sort bookings by different criteria
 * @param {Array} bookings - Array of bookings to sort
 * @param {string} sortBy - Sort criteria ('dogName', 'checkIn', 'checkOut', 'duration')
 * @param {string} sortDirection - Sort direction ('asc' or 'desc')
 * @returns {Array} Sorted bookings array
 */
export function sortBookings(bookings, sortBy = 'checkIn', sortDirection = 'asc') {
    const sorted = [...bookings].sort((a, b) => {
        let aValue, bValue;
        
        switch (sortBy) {
            case 'dogName':
                aValue = a.dog?.name?.toLowerCase() || '';
                bValue = b.dog?.name?.toLowerCase() || '';
                break;
            case 'checkIn':
                aValue = new Date(a.checkIn);
                bValue = new Date(b.checkIn);
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            case 'checkOut':
                aValue = new Date(a.checkOut);
                bValue = new Date(b.checkOut);
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            case 'duration':
                aValue = calculateDuration(a.checkIn, a.checkOut);
                bValue = calculateDuration(b.checkIn, b.checkOut);
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            case 'ownerName':
                aValue = a.dog?.owners?.[0]?.name?.toLowerCase() || '';
                bValue = b.dog?.owners?.[0]?.name?.toLowerCase() || '';
                break;
            default:
                aValue = '';
                bValue = '';
        }
        
        if (typeof aValue === 'string') {
            return sortDirection === 'asc' 
                ? aValue.localeCompare(bValue) 
                : bValue.localeCompare(aValue);
        }
        
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    return sorted;
}

/**
 * Calculate duration between two dates in nights
 * @param {string|Date} checkIn - Check-in date
 * @param {string|Date} checkOut - Check-out date
 * @returns {number} Duration in nights
 */
function calculateDuration(checkIn, checkOut) {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}

/**
 * Calculate booking statistics
 * @param {Array} bookings - Array of all bookings
 * @returns {Object} Statistics object
 */
export function calculateBookingStats(bookings) {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const toDateStr = (d) => new Date(d).toISOString().slice(0, 10);

    const stats = { total: bookings.length, current: 0, upcoming: 0, past: 0 };

    bookings.forEach(booking => {
        const checkIn = toDateStr(booking.checkIn);
        const checkOut = toDateStr(booking.checkOut);
        if (checkIn <= todayStr && checkOut >= todayStr) {
            stats.current++;
        } else if (checkIn > todayStr) {
            stats.upcoming++;
        } else {
            stats.past++;
        }
    });

    return stats;
}

/**
 * Transform check-in form data for API submission
 * @param {Object} formData - Form data
 * @returns {Object} Transformed data for API
 */
export function transformBookingFormData({ dogId, checkIn, checkOut, notes, vetWeight, vetTemperature, vetNotes, vetCheckedBy }) {
    const transformedData = {
        dog: dogId,
        checkIn,
        checkOut,
        notes: notes?.trim() || ''
    };
    
    // Only include vet check-in data if at least one field is provided
    if (vetWeight || vetTemperature || vetNotes || vetCheckedBy) {
        transformedData.vetCheckIn = {
            ...(vetWeight && { weight: parseFloat(vetWeight) }),
            ...(vetTemperature && { temperature: parseFloat(vetTemperature) }),
            ...(vetNotes && { notes: vetNotes.trim() }),
            ...(vetCheckedBy && { checkedBy: vetCheckedBy.trim() })
        };
    }
    
    return transformedData;
}

/**
 * Validate check-in form data
 * @param {Object} formData - Form data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export function validateBookingForm({ dogId, checkIn, checkOut, vetWeight, vetTemperature }) {
    const errors = {};
    
    // Dog selection validation
    if (!dogId) {
        errors.dogId = 'Please select a dog';
    }
    
    // Date validation
    if (!checkIn) {
        errors.checkIn = 'Check-in date is required';
    }
    
    if (!checkOut) {
        errors.checkOut = 'Check-out date is required';
    }
    
    // Date logic validation
    if (checkIn && checkOut) {
        const checkInDate = new Date(checkIn);
        const checkOutDate = new Date(checkOut);

        if (checkInDate >= checkOutDate) {
            errors.checkOut = 'Check-out date must be after check-in date';
        }
    }
    
    // Vet check-in validation
    if (vetWeight) {
        const weight = parseFloat(vetWeight);
        if (isNaN(weight) || weight <= 0 || weight > 100) {
            errors.vetWeight = 'Weight must be a number between 0.1 and 100 kg';
        }
    }
    
    if (vetTemperature) {
        const temp = parseFloat(vetTemperature);
        if (isNaN(temp) || temp < 35 || temp > 43) {
            errors.vetTemperature = 'Temperature must be between 35°C and 43°C';
        }
    }
    
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}

/**
 * Format date for input fields
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string (YYYY-MM-DD)
 */
export function formatInputDate(date) {
    if (!date) return '';
    return new Date(date).toISOString().split('T')[0];
}

/**
 * Get minimum date for date inputs (today)
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export function getMinDate() {
    const d = new Date();
    return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
}


/**
 * Group bookings that belong to the same multi-dog reservation, keeping the
 * order of first appearance. Bookings without a reservationId stand alone.
 * @param {Array} bookings - Bookings to group
 * @returns {Array<Array>} One array of bookings per reservation
 */
export function groupByReservation(bookings) {
    const groups = new Map();
    bookings.forEach(booking => {
        const key = booking.reservationId || booking._id;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(booking);
    });
    return [...groups.values()];
}

/**
 * The link token for a booking's intake form. Older bookings may predate
 * intakeToken, and the intake routes accept the booking _id in its place.
 * @param {Object} booking - Hotel booking
 * @returns {string} Token for /hotel/intake/:token
 */
export function intakeTokenFor(booking) {
    return booking.intakeToken || booking._id;
}
